import React from "react"
import { useCall } from "contexts/Call"
import { useAlertMessage } from "hooks/useAlertMessage"
import { isEmpty, isEqual } from "lodash"
import { createContext, memo, useCallback, useEffect, useMemo, useState } from "react"
import { createContextHook } from "utils/contextHelpers"
import { dataService } from "utils/dataService"
import { getLoggerInstance } from "utils/logger"
import { toRedacted } from "martti-agent-services"
import { NON_BILLABLE_OPTIONS } from "./constants"

import { usePartners } from "./hooks/usePartners"
import { useAccessCode } from "./hooks/useAccessCode"
import { useCustomFields } from "./hooks/useCustomFields"
import { useCallFormFields } from "./hooks/useCallFormFields"
import { useDepartmentsForForm } from "./hooks/useDepartmentsForForm"
import { usePartnerAccessCode } from "hooks/usePartnerAccessCode"
import { useDebouncedForm } from "hooks/useDebouncedForm"
import { usePureCallback } from "hooks/usePureCallback"
import { useIntakeQuestionsForInteraction } from "hooks/useIntakeQuestionsForInteraction"
import { useCurrentEncounter } from "./hooks/useCurrentEncounter"
import { callFormDataManager } from "./CallFormDataManager"
import { useConfigFlag } from "@cloudbreakus/featurebang-react"
import {
  createAnswersFromFieldNames,
  mergeIntakeAnswers,
  formatAnswers,
  formatAnswersRedacted,
  parseAnswerValue,
} from "./utils/intakeAnswers"
const { HttpErrors } = require("@cloudbreakus/network-utilities")

const logger = getLoggerInstance()

const CallFormDataContext = createContext()

export const _showSpinner = (bouncing, refreshing, saved) => bouncing || refreshing || !saved

const _CallFormDataProvider = ({ children }) => {
  const { answeredCall, exitConfirmation } = useCall()
  const { showErrorToast } = useAlertMessage()
  const debounceMs = useConfigFlag("callsheet.saveDelayMilliseconds", 1500)
  const [serverState, setServerState] = useState({})
  const [changeDetected, setChangeDetected] = useState(false)
  const [validatedAccessCode, setValidatedAccessCode] = useState(false)
  const [refreshing, setRefreshing] = useState(false)
  const [wizardStatus, setWizardStatus] = useState("partial") // options: "empty", "partial", "complete"
  const [intakeAnswers, setIntakeAnswers] = useState({})
  const [intakeAnswersChangeDetected, setIntakeAnswersChangeDetected] = useState(false)
  const [pendingIntakeRefetch, setPendingIntakeRefetch] = useState(false)

  const {
    refreshCallSheet,

    interactionId,
    interactionName,
    department,
    departmentName,
    language,
    nonBillableReasons,
    unitId,
    partnerId,
    interactionPartnerId,
    originalPartnerId,
    originalPartnerName,
    patientFirstName,
    patientLastName,
    patientMRN,
    patientDOB,
    providerFirstName,
    providerLastName,
    setPartnerId,
    setDepartment,
    setNonBillableReasons,
    setPatientFirstName,
    setPatientLastName,
    setPatientMRN,
    setPatientDOB,
    setProviderFirstName,
    setProviderLastName,

    phoneNumberAC,
    bchOaklandHospitalConfirmation,
    location,
    memberIdNumber,
    nyuAccessCode,
    sentaraAccessCode,
    visitId,
    callerFirstName,
    callerLastName,
    setVisitId,
    setSentaraAccessCode,
    setNyuAccessCode: setNYUAccessCode,
    setMemberIdNumber,
    setLocation,
    setPhoneNumberAC,
    setBchOaklandHospitalConfirmation: setBCHOaklandHospitalConfirmation,
    setCallerFirstName,
    setCallerLastName,

    customerPhoneNumber,
    ivrAccessCode,
    meetingUrl,

    showCollectLocation,
    showCollectDepartment,
    setShowCollectDepartment,
  } = useCallFormFields(answeredCall, setServerState)

  const {
    currentEncounterId,
    isPastFirstEncounter,
    canStartNextPatient,
    startNextPatient,
    loading: isFetchingEncounter,
  } = useCurrentEncounter({ interactionId })

  const { accessCodeObject, loadingAccessCode, getPartnerAccessCode } = usePartnerAccessCode(
    partnerId || originalPartnerId || interactionPartnerId,
    validatedAccessCode
  )

  const partnerIdForTree = originalPartnerId ?? interactionPartnerId
  const { partners, partnersLoading } = usePartners({ partnerId: partnerIdForTree, showCollectLocation, answeredCall })

  const { accessCode: customFieldsData, departmentId } = useCustomFields(unitId, accessCodeObject, answeredCall)

  const { departmentOptions } = useDepartmentsForForm(
    setDepartment,
    setServerState,
    partnerId || interactionPartnerId,
    departmentId,
    answeredCall
  )

  const {
    questions: intakeQuestions,
    refetch: intakeRefetch,
    error: intakeError,
    loading: intakeLoading,
  } = useIntakeQuestionsForInteraction({
    interactionId,
  })

  const accessCodeValidationData = useAccessCode({
    validatedAccessCode,
    setValidatedAccessCode,
    ivrAccessCode,
    unitId,
    answeredCall,
    interactionId,
    accessCodeObject,
    loadingAccessCode,
    getPartnerAccessCode,
    refreshCallSheet,
    intakeQuestions,
    intakeRefetch,
    intakeError,
    intakeLoading,
    intakeAnswers,
  })

  const intakeAnswerMode = useConfigFlag("callsheet.intakeAnswer")

  const { bouncing, onDebouncedChange } = useDebouncedForm(debounceMs, setChangeDetected)

  const { bouncing: intakeAnswersBouncing, onDebouncedChange: onDebouncedIntakeAnswerChange } = useDebouncedForm(
    debounceMs,
    setIntakeAnswersChangeDetected
  )

  const clientState = useMemo(
    () => ({
      nonBillableReason: nonBillableReasons === NON_BILLABLE_OPTIONS.NONE.key ? null : nonBillableReasons,
      departmentId: department,
      partnerId,
      patientFirstName,
      patientLastName,
      patientMRN,
      patientDOB,
      phoneNumberAC,
      providerFirstName,
      providerLastName,
      bchOaklandHospitalConfirmation,
      location,
      memberIdNumber,
      nyuAccessCode,
      sentaraAccessCode,
      visitId,
      callerFirstName,
      callerLastName,
    }),
    [
      nonBillableReasons,
      department,
      partnerId,
      patientFirstName,
      patientLastName,
      patientMRN,
      patientDOB,
      phoneNumberAC,
      providerFirstName,
      providerLastName,
      bchOaklandHospitalConfirmation,
      location,
      memberIdNumber,
      nyuAccessCode,
      sentaraAccessCode,
      visitId,
      callerFirstName,
      callerLastName,
    ]
  )

  const saved = useMemo(
    () => (isEmpty(serverState) ? true : isEqual(serverState, clientState)),
    [serverState, clientState]
  )

  const saveCallSheet = useCallback(
    (payload) => {
      return dataService.Interactions.CallSheet.set({
        interactionId,
        payload,
      })
    },
    [interactionId]
  )

  const submitCallFormData = useCallback(async () => {
    let payload
    try {
      const includePartnerId = partnerId && partnerId !== originalPartnerId
      payload = {
        nonBillableReason: nonBillableReasons === NON_BILLABLE_OPTIONS.NONE.key ? null : nonBillableReasons,
        departmentId: department,
        ...(includePartnerId && { partnerId }),
        patientFirstName,
        patientLastName,
        patientMRN,
        patientDOB,
        phoneNumberAC,
        providerFirstName,
        providerLastName,
        bchOaklandHospitalConfirmation,
        location,
        memberIdNumber,
        nyuAccessCode,
        sentaraAccessCode,
        visitId,
        callerFirstName,
        callerLastName,
      }
      const prevState = serverState
      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error("Network timeout: saveCallSheet took too long")), 15000)
      )
      await Promise.race([saveCallSheet(payload), timeoutPromise])
      setServerState(payload)
      logger.info(`Call sheet updated`, toRedacted(payload))
      if (
        (payload.partnerId && payload.partnerId !== prevState.partnerId) ||
        (payload.departmentId && payload.departmentId !== prevState.departmentId)
      ) {
        intakeRefetch()
      } else {
        setPendingIntakeRefetch(false)
      }
    } catch (err) {
      if (!(err instanceof HttpErrors.ThrottledNetworkError)) {
        showErrorToast("Call sheet failed to update, please try again.")
      }
      logger.error(`Call sheet failed to update`, err, toRedacted(payload))
      setPendingIntakeRefetch(false)
    }
    callFormDataManager.onSubmitted()
  }, [
    originalPartnerId,
    nonBillableReasons,
    department,
    partnerId,
    patientFirstName,
    patientLastName,
    patientMRN,
    patientDOB,
    phoneNumberAC,
    providerFirstName,
    providerLastName,
    bchOaklandHospitalConfirmation,
    location,
    memberIdNumber,
    nyuAccessCode,
    sentaraAccessCode,
    visitId,
    callerFirstName,
    callerLastName,
    saveCallSheet,
    showErrorToast,
    intakeRefetch,
    serverState,
  ])

  const onNonBillableReasons = onDebouncedChange(setNonBillableReasons)
  const debouncedSetPartnerId = onDebouncedChange(setPartnerId)
  const onPartner = useCallback(
    (id) => {
      const findInTree = (nodes, targetId) => {
        for (const node of nodes || []) {
          if (node.value === targetId) return node
          const found = findInTree(node.children, targetId)
          if (found) return found
        }
        return null
      }
      const selected = findInTree(partners, id)
      if (selected) {
        setShowCollectDepartment(selected.showCollectDepartment ?? true)
      }
      if (id !== partnerId) {
        setDepartment("")
        setPendingIntakeRefetch(true)
      }
      debouncedSetPartnerId(id)
    },
    [partners, partnerId, debouncedSetPartnerId, setShowCollectDepartment, setDepartment]
  )
  const debouncedSetDepartment = onDebouncedChange(setDepartment)
  const onDepartment = useCallback(
    (val) => {
      if (val !== department) {
        setPendingIntakeRefetch(true)
      }
      debouncedSetDepartment(val)
    },
    [department, debouncedSetDepartment]
  )
  const onPatientFirstName = onDebouncedChange(setPatientFirstName)
  const onPatientLastName = onDebouncedChange(setPatientLastName)
  const onPatientMRN = onDebouncedChange(setPatientMRN)
  const onProviderFirstName = onDebouncedChange(setProviderFirstName)
  const onProviderLastName = onDebouncedChange(setProviderLastName)

  const onVisitId = onDebouncedChange(setVisitId)
  const onSentaraAccessCode = onDebouncedChange(setSentaraAccessCode)
  const onNYUAccessCode = onDebouncedChange(setNYUAccessCode)
  const onMemberIdNumber = onDebouncedChange(setMemberIdNumber)
  const onLocation = onDebouncedChange(setLocation)
  const onPhoneNumberAccessCode = onDebouncedChange(setPhoneNumberAC)
  const onBCHOaklandHospital = onDebouncedChange(setBCHOaklandHospitalConfirmation)
  const onCallerFirstName = onDebouncedChange(setCallerFirstName)
  const onCallerLastName = onDebouncedChange(setCallerLastName)

  const onPatientDOB = useMemo(() => {
    return onDebouncedChange(setPatientDOB)
  }, [onDebouncedChange, setPatientDOB])

  const saveIntakeAnswers = useCallback(
    async (intakeQuestions) => {
      try {
        const answers = formatAnswers(intakeQuestions, intakeAnswers)
        if (!answers?.length || !interactionId) return
        const timeoutPromise = new Promise((_, reject) =>
          setTimeout(() => reject(new Error("Network timeout: upsertIntakeAnswers took too long")), 15000)
        )
        const currentEncounter = await dataService.IntakeQuestions.getCurrentEncounter({ interactionId })
        const currentEncounterId = currentEncounter?.data?.id
        await Promise.race([
          dataService.IntakeQuestions.upsertAnswers({ interactionId, answers, currentEncounterId }),
          timeoutPromise,
        ])
        const answersRedacted = formatAnswersRedacted(intakeQuestions, intakeAnswers)
        logger.info(`Intake answers updated for interaction ${interactionId}`, answersRedacted)
      } catch (err) {
        if (!(err instanceof HttpErrors.ThrottledNetworkError)) {
          showErrorToast("Failed to save answers, please try again.")
        }
        logger.error(`Failed to save intake answers for interaction ${interactionId}`, err)
      }
    },
    [interactionId, intakeAnswers, showErrorToast]
  )

  const onDebouncedSetIntakeAnswer = useMemo(
    () => onDebouncedIntakeAnswerChange(setIntakeAnswers),
    [onDebouncedIntakeAnswerChange]
  )

  useEffect(() => {
    if (!intakeLoading) {
      setPendingIntakeRefetch(false)
    }
  }, [intakeLoading, intakeQuestions, intakeError])

  useEffect(() => {
    if (!interactionId || !intakeAnswersChangeDetected || !intakeQuestions || intakeQuestions.length === 0) {
      return
    }
    const doSubmit = exitConfirmation ? true : !intakeAnswersBouncing
    if (!doSubmit) {
      return
    }
    setIntakeAnswersChangeDetected(false)
    saveIntakeAnswers(intakeQuestions)
  }, [
    interactionId,
    intakeAnswersBouncing,
    intakeAnswersChangeDetected,
    exitConfirmation,
    intakeQuestions,
    saveIntakeAnswers,
  ])

  const onIntakeQuestionChange = useCallback(
    (questionId, value) => {
      onDebouncedSetIntakeAnswer((prev) => ({ ...prev, [questionId]: value }))
    },
    [onDebouncedSetIntakeAnswer]
  )

  const createInteractionAnswers = useCallback(() => {
    return createAnswersFromFieldNames(intakeQuestions, {
      patientFirstName,
      patientLastName,
      patientMRN,
      patientDOB,
      providerFirstName,
      providerLastName,
      phoneNumberAC,
      bchOaklandHospitalConfirmation,
      location,
      memberIdNumber,
      nyuAccessCode,
      sentaraAccessCode,
      visitId,
      callerFirstName,
      callerLastName,
    })
  }, [
    intakeQuestions,
    patientFirstName,
    patientLastName,
    patientMRN,
    patientDOB,
    providerFirstName,
    providerLastName,
    phoneNumberAC,
    bchOaklandHospitalConfirmation,
    location,
    memberIdNumber,
    nyuAccessCode,
    sentaraAccessCode,
    visitId,
    callerFirstName,
    callerLastName,
  ])

  useEffect(() => {
    if (!interactionId || !intakeQuestions || intakeQuestions.length === 0) {
      setIntakeAnswers({})
      return
    }

    if (isFetchingEncounter) {
      return
    }

    const loadIntakeAnswers = async () => {
      try {
        let tableAnswers = {}
        let interactionAnswers = {}

        if (intakeAnswerMode === "new" || intakeAnswerMode === "both") {
          const timeoutPromise = new Promise((_, reject) =>
            setTimeout(() => reject(new Error("Network timeout: getIntakeAnswers took too long")), 15000)
          )
          const response = await Promise.race([
            dataService.IntakeQuestions.getAnswers({ interactionId, intakeQuestions }),
            timeoutPromise,
          ])
          tableAnswers = response?.data || {}
        }

        if (intakeAnswerMode === "old" || intakeAnswerMode === "both") {
          interactionAnswers = createInteractionAnswers()
        }

        const mergedAnswers = mergeIntakeAnswers({
          intakeQuestions,
          tableAnswers,
          interactionAnswers,
          mode: intakeAnswerMode,
          isPastFirstEncounter,
          parseAnswerValue,
        })

        setIntakeAnswers(mergedAnswers)
      } catch (err) {
        logger.error(`Failed to load intake answers for interaction ${interactionId}`, err)
      }
    }

    loadIntakeAnswers()
  }, [
    interactionId,
    intakeAnswerMode,
    createInteractionAnswers,
    intakeQuestions,
    currentEncounterId,
    isPastFirstEncounter,
    isFetchingEncounter,
  ])

  useEffect(() => {
    if (!interactionId || !changeDetected) {
      return
    }
    callFormDataManager.detectChange()
    const doSubmit = exitConfirmation ? true : !bouncing
    if (!doSubmit) {
      return
    }
    setChangeDetected(false)
    callFormDataManager.submit()
  }, [interactionId, bouncing, changeDetected, exitConfirmation])

  const triggerSubmit = usePureCallback(submitCallFormData)
  const triggerRefresh = usePureCallback(refreshCallSheet)

  useEffect(() => {
    callFormDataManager.on("submit", triggerSubmit)
    callFormDataManager.on("refresh", triggerRefresh)
    callFormDataManager.on("running", setRefreshing)

    return () => {
      callFormDataManager.off("submit", triggerSubmit)
      callFormDataManager.off("refresh", triggerRefresh)
      callFormDataManager.off("running", setRefreshing)
    }
  }, [triggerSubmit, triggerRefresh])

  const _refreshCallSheet = useCallback(() => {
    callFormDataManager.refresh()
  }, [])

  useEffect(() => {
    if (!interactionId) {
      return
    }
    return () => callFormDataManager.wrapUp()
  }, [interactionId])

  const showSpinner = useMemo(() => _showSpinner(bouncing, refreshing, saved), [saved, refreshing, bouncing])

  const partnerLocationId = useMemo(() => {
    if (!showCollectLocation) {
      return partnerId
    }
    if (originalPartnerId && partnerId === originalPartnerId) {
      return ""
    }
    return partnerId || ""
  }, [partnerId, originalPartnerId, showCollectLocation])

  const contextValue = useMemo(
    () => ({
      ...accessCodeValidationData,
      ...customFieldsData,
      interactionId: interactionName,
      language,
      unitId,
      partnerLocations: partners,
      partnerLocationId,
      partnerName: originalPartnerName,
      department,
      departmentName,
      patientFirstName,
      patientLastName,
      patientMRN,
      patientDOB,
      providerFirstName,
      providerLastName,
      departmentOptions,
      nonBillableReasons,

      onPatientDOB,
      onNonBillableReasons,
      onPartnerLocation: onPartner,
      onDepartment,
      onPatientFirstName,
      onPatientLastName,
      onPatientMRN,
      onProviderFirstName,
      onProviderLastName,
      refreshCallSheet: _refreshCallSheet,
      saved: !showSpinner,
      wizardStatus,
      setWizardStatus,
      phoneNumberAC,
      bchOaklandHospitalConfirmation,
      location,
      memberIdNumber,
      nyuAccessCode,
      sentaraAccessCode,
      visitId,
      callerFirstName,
      callerLastName,
      onVisitId,
      onSentaraAccessCode,
      onNYUAccessCode,
      onMemberIdNumber,
      onLocation,
      onPhoneNumberAccessCode,
      onBCHOaklandHospital,
      onCallerFirstName,
      onCallerLastName,

      customerPhoneNumber,
      ivrAccessCode,
      meetingUrl,
      showCollectLocation,
      showCollectDepartment,
      partnersLoading,
      intakeQuestions,
      intakeRefetch,
      intakeError,
      intakeAnswers,
      isFetchingIntakeQuestions: pendingIntakeRefetch || intakeLoading,
      onIntakeQuestionChange,
      startNextPatient,
      canStartNextPatient,
      currentEncounterId,
      isPastFirstEncounter,
      isFetchingEncounter,
    }),
    [
      accessCodeValidationData,
      customFieldsData,
      interactionName,
      language,
      unitId,
      partners,
      partnerLocationId,
      originalPartnerName,
      department,
      departmentName,
      patientFirstName,
      patientLastName,
      patientMRN,
      patientDOB,
      providerFirstName,
      providerLastName,
      departmentOptions,
      nonBillableReasons,
      onPatientDOB,
      onNonBillableReasons,
      onPartner,
      onDepartment,
      onPatientFirstName,
      onPatientLastName,
      onPatientMRN,
      onProviderFirstName,
      onProviderLastName,
      _refreshCallSheet,
      showSpinner,
      wizardStatus,
      setWizardStatus,
      phoneNumberAC,
      bchOaklandHospitalConfirmation,
      location,
      memberIdNumber,
      nyuAccessCode,
      sentaraAccessCode,
      visitId,
      callerFirstName,
      callerLastName,
      onVisitId,
      onSentaraAccessCode,
      onNYUAccessCode,
      onMemberIdNumber,
      onLocation,
      onPhoneNumberAccessCode,
      onBCHOaklandHospital,
      onCallerFirstName,
      onCallerLastName,
      customerPhoneNumber,
      ivrAccessCode,
      meetingUrl,
      showCollectLocation,
      showCollectDepartment,
      partnersLoading,
      intakeQuestions,
      intakeRefetch,
      intakeError,
      intakeAnswers,
      pendingIntakeRefetch,
      intakeLoading,
      onIntakeQuestionChange,
      startNextPatient,
      canStartNextPatient,
      currentEncounterId,
      isPastFirstEncounter,
      isFetchingEncounter,
    ]
  )

  return <CallFormDataContext.Provider value={contextValue}>{children}</CallFormDataContext.Provider>
}

export const CallFormDataProvider = memo(_CallFormDataProvider)

export const useCallFormData = createContextHook(
  CallFormDataContext,
  "useCallFormData must be used within a CallFormDataContext"
)

export default CallFormDataContext
